import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { AuditRecord } from '@trustero/trustero-api-web/lib/audit/audit_pb'
import { useModalState } from 'src/Modal/ModalStateContext'
import { ModalFormId } from 'src/components/ModalForms'
import { StandardOpenModalButton } from 'src/components/ModalForms/ModalButtons'
import {
  StandardButtonSize,
  StandardButtonVariant,
} from 'src/components/Reusable/Buttons'
import { IndexPage } from 'src/components/Reusable/IndexPage'
import { useAudits } from 'src/components/async/model/audit/useAudit'
import { urlHasFragment } from 'src/Utils/url.helpers'
import { UrlFragments } from 'src/Utils/globalEnums'
import { TableListLoader } from 'src/components/Reusable/ContentLoaders/TableListLoader'
import { DeleteAuditConfirmation } from 'src/components/ModalForms/Audit/DeleteAuditConfirmation'
import { AddOrEditAuditForm } from 'src/components/ModalForms/Audit/AddOrEditAuditForm'
import { AddReportForm } from 'src/components/ModalForms/Report/AddReport'
import { FilterBar } from 'src/components/Reusable/IndexPage/FilterBar/FilterBar'
import { FilterBarConfigs } from 'src/components/Reusable/IndexPage/FilterBar/FilterBar.constants'
import { INDEX_PAGE_WITH_FILTERS } from 'src/components/Reusable/IndexPage/IndexPage.constants'
import { FilterBarValuesProvider } from 'src/components/Reusable/IndexPage/FilterBar/FilterBar.context'
import { useApplySearch } from 'src/Utils/search/search.hooks'
import { FlexAlign, FlexRow } from 'src/components/Reusable/Flex'
import { NoFilterResults } from 'src/components/PageLayout/NoFilterResults'
import { usePaginateItemsWithNavigate } from 'src/components/Reusable/paginate/paginate.hooks'
import { PaginateButtonsGroup } from 'src/components/Reusable/paginate/PaginateButtons'
import { AuditIndexGrid } from './AuditIndexGrid'
import { AddAuditButton } from './AuditIndexPage.styles'
import {
  useAuditFiltersAndSearchData,
  useFilteredAudits,
} from './AuditIndex.hooks'

export const AuditsIndexRoute = 'audits'

const AUDIT_INDEX_PAGE_SIZE = 20

export const AuditIndexPage = (): JSX.Element => {
  return (
    <FilterBarValuesProvider>
      <AuditIndexPageContent />
    </FilterBarValuesProvider>
  )
}

const AuditIndexPageContent = (): JSX.Element => {
  const [searchParams] = useSearchParams()
  const pageCount = Number(searchParams.get('pc'))
  const { data: audits, isLoading } = useAudits()

  const [openModalOnLoad, setOpenModalOnLoad] = useState(
    urlHasFragment(UrlFragments.ADD_AUDIT),
  )
  const { openModal, activeModal } = useModalState()

  useEffect(() => {
    if (openModalOnLoad) {
      openModal(ModalFormId.ADD_AUDIT)
      // clear the fragment
      setOpenModalOnLoad(false)
    }
  }, [openModalOnLoad, openModal])

  useEffect(() => {
    if (
      !openModalOnLoad &&
      activeModal === ModalFormId.ADD_AUDIT &&
      urlHasFragment(UrlFragments.ADD_AUDIT)
    ) {
      window.history.replaceState({}, document.title, window.location.pathname)
    }
  }, [activeModal, openModalOnLoad])

  const auditObjects = audits?.getItemsList() || []

  // Apply search first, then filters
  const searchableAudits = useAuditFiltersAndSearchData(auditObjects)
  const { data: matchingAudits } = useApplySearch(searchableAudits)
  const filteredAudits = useFilteredAudits(matchingAudits)
  const { paginatedItems, currentPage, nextPage, prevPage } =
    usePaginateItemsWithNavigate<AuditRecord>({
      items: filteredAudits,
      pageSize: pageCount > 0 ? pageCount : AUDIT_INDEX_PAGE_SIZE,
    })

  return (
    <IndexPage
      title="Audits"
      actions={
        <FlexRow align={FlexAlign.FLEX_START} justify={FlexAlign.SPACE_BETWEEN}>
          <span>Track and manage your audits</span>
          <AddAuditButton>
            <StandardOpenModalButton
              variant={StandardButtonVariant.PRIMARY}
              size={StandardButtonSize.SMALL}
              modalId={ModalFormId.ADD_AUDIT}
              text="Add Audit"
            />
          </AddAuditButton>
        </FlexRow>
      }
      filterBar={
        auditObjects.length > 0 ? (
          <FilterBar
            config={FilterBarConfigs[INDEX_PAGE_WITH_FILTERS.AUDIT]}
            searchBarOptions={{ withContentSearch: false }}
          />
        ) : (
          <></>
        )
      }
      indexGrid={
        <>
          {!isLoading || !audits ? (
            <>
              {filteredAudits.length > 0 ? (
                <AuditIndexGrid
                  audits={paginatedItems}
                  isEmpty={auditObjects.length === 0}
                />
              ) : (
                <NoFilterResults />
              )}
            </>
          ) : (
            <TableListLoader />
          )}
        </>
      }
      pagination={
        <PaginateButtonsGroup
          totalItems={filteredAudits.length}
          pageSize={pageCount > 0 ? pageCount : AUDIT_INDEX_PAGE_SIZE}
          currentPage={currentPage}
          onPrevClick={prevPage}
          onNextClick={nextPage}
        />
      }
      modals={[
        <DeleteAuditConfirmation key={ModalFormId.DELETE_AUDIT} />,
        <AddOrEditAuditForm key={ModalFormId.ADD_AUDIT} />,
        <AddReportForm key={ModalFormId.ADD_CLOSED_AUDIT_FINAL_REPORT} />,
      ]}
    />
  )
}
